import * as React from "react";
import moment from "moment";
import {
  Typography,
  makeStyles,
  Theme,
  IconButton,
  Dialog,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import GolfCourseIcon from "@material-ui/icons/GolfCourse";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DialogToolbar from "../SharedComponents/DialogToolbar";

import { Player } from "types/player";
import { Game } from "types/game";
import { Tournament, ScoreSystem } from "types/tournament";
import { usePlayerTMGames, PlayerTMGameType } from "hooks/usePlayerTMGames";
import { useSelector } from "hooks/useSelector";
import { showAlertDialog } from "../SharedComponents/AlertDialog";
import IndividualLeaderBoard from "components/leaderBoard/IndividualLeaderBoard";
import { showAuxiliaryDialog } from "components/SharedComponents/AuxiliaryDialog";
import ScoreCardView from "components/ScoreCard/ScoreCardSubmition";
import ModernScoreCardView from "components/ScoreCard/ModernScoreCardView";
import { getGrossStyle } from "utils";
import { getDialogBackdropProps, getDialogLayout } from "../../consts/dialog";
import GameCodeCapsule from "../Game/GameCodeCapsule";
import Config from "../../config";

export interface TournamentHoleByHoleDialogProps {
  player: Player;
  points: number;
  tournament: Tournament;
}

interface StateType {
  dialog?: string;
  player?: Player;
  game?: Game;
}

const sortedHoleNumbers = (holes?: number[]) => (holes?.length ? [...holes].sort((a, b) => a - b) : []);

const getRoundScore = (game: PlayerTMGameType, playerId: string): number | null => {
  const playerData = game.players?.[playerId];
  if (!playerData?.points) return null;
  const { totalPoint, totalGross, totalNett } = playerData.points;
  if (typeof totalPoint === "number" && totalPoint > 0) return totalPoint;
  if (typeof totalGross === "number" && totalGross > 0) return totalGross;
  if (typeof totalNett === "number" && totalNett > 0) return totalNett;
  const fallback = totalPoint ?? totalGross ?? totalNett;
  return typeof fallback === "number" ? fallback : null;
};

const countRoundBlobs = (game: PlayerTMGameType, playerId: string): number => {
  const playerData = game.players?.[playerId];
  if (!playerData || !playerData.points || !playerData.points.holes) return 0;

  const keys = Object.keys(playerData.points.holes);
  const isStroke = game.competition && game.competition.scoringFormat === Config.SCORING_FORMAT.STROKE;
  const scoreCardId = playerData.teeColor && playerData.teeColor.scoreCardId;
  const holesParams =
    scoreCardId == null
      ? []
      : (game.scoreCards || []).find(sc => String(sc._id) === String(scoreCardId))?.holesParams || [];

  let count = 0;
  for (let i = 0; i < keys.length; i++) {
    const hole = playerData.holes[keys[i] as any];
    if (!hole) continue;
    const holePoints = playerData.points.holes[keys[i] as any];
    const point = holePoints ? holePoints.point : 0;
    const nett = holePoints && holePoints.nett;
    const gross = hole.gross;
    const par = holesParams[i] && holesParams[i].par;
    const isBlob = isStroke
      ? String(gross) === "PU" || (par != null && nett != null && nett >= par + 2)
      : point === 0;
    if (isBlob) count += 1;
  }
  return count;
};

const TournamentHoleByHoleDialog: React.FunctionComponent<TournamentHoleByHoleDialogProps> = props => {
  const scorecardLayout = getDialogLayout({ fullScreen: true, maxWidth: "md" });
  const classes = useStyles({ scorecardFullScreen: scorecardLayout.fullScreen });
  const user = useSelector(state => state.auth.user);
  const { player, tournament, points } = props;
  const { games, isEclectic, holes, isLoading } = usePlayerTMGames(player._id, tournament._id);
  const [state, setState] = React.useState<StateType>({});
  const isTotalBlobs = tournament.selectedScoreSystem === ScoreSystem.TOTAL_BLOBS;

  const showScoreCardDialog = (gameId: string) => {
    const selectedGame = games.find(g => g._id === gameId);
    if (!selectedGame) return;

    const selectedPlayer = selectedGame.players[player._id];
    const isOrganizer = selectedGame.organizers.some(organizer => organizer._id === user._id);

    if (isOrganizer) {
      showAlertDialog(
        "Submission",
        "Review Submission Card.",
        () => {
          setState({
            dialog: "submission",
            player: selectedPlayer,
            game: selectedGame,
          });
        },
        () => {
          setState({
            dialog: "viewCard",
            player: selectedPlayer,
            game: selectedGame,
          });
        },
      );
    } else {
      setState({
        dialog: "viewCard",
        player: selectedPlayer,
        game: selectedGame,
      });
    }
  };

  const openGameLeaderboard = (gameId: string) => {
    showAuxiliaryDialog(
      <IndividualLeaderBoard gameId={gameId} activeUserId={player._id} />,
      "xl",
      true,
      true,
      true,
    );
  };

  const seasonLabel = `${moment(tournament.seasonStart).format("ll")} – ${moment(tournament.seasonEnd).format("ll")}`;

  return (
    <>
      <div className={classes.root}>
        <header className={classes.header}>
          <div>
            <Typography component="p" className={classes.tournamentName}>
              {tournament.name}
            </Typography>
            <Typography component="p" className={classes.tournamentDates}>
              {seasonLabel}
            </Typography>
          </div>
          <Typography
            component="p"
            className={classes.total}
            aria-label={isTotalBlobs ? `Tournament total: ${points}` : `Tournament total: ${points} points`}
          >
            <span className={classes.totalValue}>{points}</span>
            {!isTotalBlobs ? <span className={classes.totalLabel}>pts</span> : null}
          </Typography>
        </header>

        {isLoading ? (
          <div className={classes.skeletonList} aria-busy aria-label="Loading rounds">
            {[0, 1, 2].map(i => (
              <Skeleton key={i} variant="rect" className={classes.skeletonRow} animation="wave" />
            ))}
          </div>
        ) : null}

        {!isLoading && isEclectic && holes.length > 0 ? (
          <section className={classes.eclecticSection} aria-label="Best scores by hole">
            <Typography component="h3" className={classes.sectionLabel}>
              Best by hole
            </Typography>
            <div className={classes.holeScroll}>
              <div className={classes.holeGrid}>
                {holes.map(hole => (
                  <button
                    key={hole.holeNumber}
                    type="button"
                    className={classes.holeColumn}
                    onClick={() => showScoreCardDialog(hole.gameId)}
                    aria-label={`Hole ${hole.holeNumber}, par ${hole.par}, score ${hole.gross}`}
                  >
                    <span className={classes.holeNum}>{hole.holeNumber}</span>
                    <span className={classes.holePar}>Par {hole.par}</span>
                    <span className={classes.holeGross} style={getGrossStyle(hole.par, hole.gross)}>
                      {hole.gross === "mismatch" ? "–" : hole.gross}
                    </span>
                    <span className={classes.holePts}>
                      {!hole.gross || hole.gross === "mismatch" ? "–" : hole.score}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {!isLoading && games.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography className={classes.emptyTitle}>No rounds yet</Typography>
            <Typography className={classes.emptyBody}>
              Completed scorecards linked to this tournament will appear here.
            </Typography>
          </div>
        ) : null}

        {!isLoading && games.length > 0 ? (
          <section className={classes.roundsSection} aria-label="Tournament rounds">
            <Typography component="h3" className={classes.sectionLabel}>
              {isEclectic ? "Contributing rounds" : "Rounds"}
              <span className={classes.roundCount}>{games.length}</span>
            </Typography>
            <ul className={classes.roundList}>
              {games.map(gm => {
                const blobCount = countRoundBlobs(gm, player._id);
                const roundScore = !isEclectic && !isTotalBlobs ? getRoundScore(gm, player._id) : null;
                const usedHoles = sortedHoleNumbers(gm.holes);
                const holesUsedText = usedHoles.length
                  ? `Holes used: ${usedHoles.join(", ")}`
                  : "None from this round";
                const openScorecard = () => showScoreCardDialog(gm._id);
                const onRoundRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openScorecard();
                  }
                };
                const isRoundClickable = isEclectic || isTotalBlobs;

                return (
                  <li key={gm._id} className={classes.roundItem}>
                    <div
                      className={
                        isRoundClickable ? `${classes.roundRow} ${classes.roundRowClickable}` : classes.roundRow
                      }
                      {...(isRoundClickable
                        ? {
                            role: "button",
                            tabIndex: 0,
                            onClick: openScorecard,
                            onKeyDown: onRoundRowKeyDown,
                            "aria-label": isTotalBlobs
                              ? `${gm.name}. ${blobCount} blobs`
                              : `${gm.name}. ${holesUsedText}`,
                          }
                        : {})}
                    >
                      <div className={classes.roundMain}>
                        <span className={classes.roundName}>{gm.name}</span>
                        <div className={classes.roundMeta}>
                          <span className={classes.roundDate}>{moment(gm.date).format("ll")}</span>
                          {gm.code ? <GameCodeCapsule code={gm.code} size="small" /> : null}
                        </div>
                        {isEclectic ? (
                          <div className={classes.usedHoles}>
                            <span className={classes.usedHolesLabel}>Holes used</span>
                            {usedHoles.length > 0 ? (
                              <div className={classes.holePills} aria-hidden>
                                {usedHoles.map(n => (
                                  <span key={n} className={classes.holePill}>
                                    {n}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className={classes.usedHolesEmpty}>None from this round</span>
                            )}
                          </div>
                        ) : null}
                      </div>
                      {isEclectic ? (
                        <span className={classes.roundOpen} aria-hidden>
                          <ChevronRightIcon className={classes.chevron} fontSize="small" />
                        </span>
                      ) : isTotalBlobs ? (
                        <div className={classes.roundActions}>
                          <span className={classes.roundScore} aria-label={`${blobCount} blobs`}>
                            {blobCount}
                          </span>
                          <span className={classes.roundOpen} aria-hidden>
                            <ChevronRightIcon className={classes.chevron} fontSize="small" />
                          </span>
                        </div>
                      ) : (
                        <div className={classes.roundActions}>
                          {roundScore != null ? (
                            <span className={classes.roundScore} aria-label={`Round score ${roundScore}`}>
                              {roundScore}
                            </span>
                          ) : null}
                          <IconButton
                            size="small"
                            color="primary"
                            className={classes.leaderboardBtn}
                            aria-label={`Open leaderboard for ${gm.name}`}
                            onClick={() => openGameLeaderboard(gm._id)}
                          >
                            <GolfCourseIcon fontSize="small" />
                          </IconButton>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
      </div>

      {(isEclectic || isTotalBlobs) && state.dialog && state.player && state.game && (
        <>
          <Dialog open={state.dialog === "submission"} fullScreen onBackdropClick={() => setState({})}>
            <ScoreCardView player={state.player} game={state.game} closeDialog={() => setState({})} />
          </Dialog>
          <Dialog
            open={state.dialog === "viewCard"}
            onClose={() => setState({})}
            BackdropProps={getDialogBackdropProps()}
            fullWidth={scorecardLayout.fullWidth}
            fullScreen={scorecardLayout.fullScreen}
            maxWidth={scorecardLayout.maxWidth}
            scroll="paper"
            classes={{ root: classes.nestedDialogRoot, paper: classes.nestedDialogPaper }}
            PaperProps={{ style: scorecardLayout.paperStyle }}
          >
            <div className={classes.nestedDialogHeader}>
              <DialogToolbar title="Scorecard" onClick={() => setState({})} />
            </div>
            <div className={classes.nestedDialogBody}>
              <ModernScoreCardView player={state.player} game={state.game} />
            </div>
          </Dialog>
        </>
      )}
    </>
  );
};

const useStyles = makeStyles<Theme, { scorecardFullScreen: boolean }, string>(theme => {
  const border = "rgba(226, 232, 240, 0.92)";
  const textMuted = "#64748b";
  const textPrimary = "#1e293b";

  return {
    root: {
      flex: 1,
      minHeight: 0,
      padding: theme.spacing(0, 2, 2),
      boxSizing: "border-box",
      backgroundColor: theme.palette.background.paper,
    },
    header: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      margin: theme.spacing(0, -2, 0),
      padding: theme.spacing(1.5, 2, 1.5),
      backgroundColor: "#f8fafc",
      borderBottom: `1px solid ${border}`,
    },
    tournamentName: {
      margin: 0,
      fontWeight: 600,
      fontSize: theme.typography.subtitle1.fontSize,
      lineHeight: 1.35,
      color: textPrimary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    tournamentDates: {
      margin: theme.spacing(0.25, 0, 0),
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 1.4,
      color: textMuted,
    },
    total: {
      margin: 0,
      flexShrink: 0,
      textAlign: "right",
      lineHeight: 1.2,
    },
    totalValue: {
      display: "block",
      fontWeight: 600,
      fontSize: theme.typography.h6.fontSize,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.primary.main,
    },
    totalLabel: {
      display: "block",
      marginTop: 2,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 500,
      color: textMuted,
    },
    sectionLabel: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      margin: 0,
      padding: theme.spacing(1.75, 0, 1),
      fontSize: theme.typography.subtitle1.fontSize,
      fontWeight: 700,
      lineHeight: 1.3,
      color: theme.palette.primary.main,
    },
    roundCount: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 22,
      height: 22,
      padding: "0 6px",
      borderRadius: 11,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontSize: "0.75rem",
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1,
    },
    skeletonList: {
      padding: theme.spacing(1, 0, 0),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
    skeletonRow: {
      height: 56,
      borderRadius: theme.shape.borderRadius,
    },
    eclecticSection: {
      paddingBottom: theme.spacing(0.5),
      borderBottom: `1px solid ${border}`,
    },
    holeScroll: {
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      paddingBottom: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
      scrollbarWidth: "thin",
      scrollbarColor: "rgba(148, 163, 184, 0.55) transparent",
      "&::-webkit-scrollbar": {
        height: 4,
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(148, 163, 184, 0.55)",
        borderRadius: 4,
      },
    },
    holeGrid: {
      display: "flex",
      flexDirection: "row",
      gap: theme.spacing(1),
      width: "max-content",
      minWidth: "100%",
    },
    holeColumn: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
      minWidth: 44,
      padding: theme.spacing(0.75, 0.5),
      border: "none",
      borderRight: `1px solid ${border}`,
      background: "transparent",
      cursor: "pointer",
      font: "inherit",
      color: "inherit",
      "&:last-child": {
        borderRight: "none",
      },
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      "&:active": {
        transform: "scale(0.97)",
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: -2,
      },
    },
    holeNum: {
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
    holePar: {
      fontSize: "0.6875rem",
      color: textMuted,
    },
    holeGross: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28,
      borderRadius: "50%",
      border: `1px solid ${theme.palette.divider}`,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
    },
    holePts: {
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 600,
      color: textMuted,
      fontVariantNumeric: "tabular-nums",
    },
    roundsSection: {
      paddingTop: theme.spacing(0.25),
    },
    roundList: {
      listStyle: "none",
      margin: 0,
      padding: 0,
    },
    roundItem: {
      margin: 0,
      padding: 0,
      borderBottom: `1px solid ${border}`,
      "&:last-child": {
        borderBottom: "none",
      },
    },
    roundRow: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      alignItems: "start",
      columnGap: theme.spacing(1),
      width: "100%",
      boxSizing: "border-box",
      padding: theme.spacing(1.25, 0),
    },
    roundRowClickable: {
      cursor: "pointer",
      borderRadius: theme.shape.borderRadius,
      transition: theme.transitions.create("background-color", {
        duration: theme.transitions.duration.shortest,
      }),
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
      },
    },
    roundMain: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 2,
      minWidth: 0,
    },
    roundMeta: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      columnGap: theme.spacing(1.5),
      rowGap: 4,
      minWidth: 0,
      marginTop: 2,
    },
    roundDate: {
      fontSize: theme.typography.caption.fontSize,
      color: textMuted,
      fontVariantNumeric: "tabular-nums",
    },
    roundName: {
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 600,
      color: textPrimary,
      lineHeight: 1.35,
    },
    roundOpen: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 24,
      height: 24,
      margin: 0,
      padding: 0,
      color: "inherit",
      pointerEvents: "none",
    },
    roundActions: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexShrink: 0,
    },
    roundScore: {
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
      color: textPrimary,
    },
    leaderboardBtn: {
      padding: 6,
    },
    usedHoles: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 6,
      marginTop: theme.spacing(1),
    },
    usedHolesLabel: {
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 600,
      lineHeight: 1.3,
      color: textMuted,
    },
    usedHolesEmpty: {
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 1.4,
      color: textMuted,
    },
    holePills: {
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
    },
    holePill: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 22,
      height: 22,
      padding: "0 5px",
      boxSizing: "border-box",
      borderRadius: 4,
      backgroundColor: "#f1f5f9",
      color: "#334155",
      fontSize: "0.6875rem",
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1,
    },
    chevron: {
      color: textMuted,
      flexShrink: 0,
    },
    emptyState: {
      padding: theme.spacing(3, 0),
      textAlign: "center",
    },
    emptyTitle: {
      margin: 0,
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 600,
      color: textPrimary,
    },
    emptyBody: {
      margin: theme.spacing(0.75, "auto", 0),
      maxWidth: 280,
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 1.5,
      color: textMuted,
    },
    nestedDialogRoot: {
      zIndex: theme.zIndex.modal + 2,
    },
    nestedDialogPaper: {
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      width: "100%",
      margin: ({ scorecardFullScreen }) => (scorecardFullScreen ? 0 : theme.spacing(2)),
      height: ({ scorecardFullScreen }) => (scorecardFullScreen ? "100%" : "auto"),
      maxHeight: ({ scorecardFullScreen }) => (scorecardFullScreen ? "100%" : "90vh"),
      maxWidth: ({ scorecardFullScreen }) =>
        scorecardFullScreen ? "100%" : "min(720px, calc(100vw - 24px))",
      borderRadius: ({ scorecardFullScreen }) => (scorecardFullScreen ? 0 : theme.shape.borderRadius),
      border: ({ scorecardFullScreen }) => (scorecardFullScreen ? "none" : `1px solid ${border}`),
    },
    nestedDialogHeader: {
      flex: "0 0 auto",
    },
    nestedDialogBody: {
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      WebkitOverflowScrolling: "touch",
      backgroundColor: theme.palette.background.default,
      paddingBottom: theme.spacing(1),
    },
  };
});

export default TournamentHoleByHoleDialog;
